import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, Loader2, CheckCircle2, Package, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Öffentliche Kunden-Ansicht: Fortschritt der EU-Fahrzeugbestellung.
 * Aufruf via /bestellung/:token — kein Login. Zeigt Händler-Branding,
 * Fortschrittsbalken, ETA und Verlauf.
 */
export default function OrderTrackingPublicPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        axios.get(`${API}/public/order-tracking/${token}`)
            .then((r) => setData(r.data))
            .catch(() => setError("Diese Bestellung wurde nicht gefunden."))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div>;
    }
    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="order-tracking-public-error">
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center max-w-sm">
                    <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">{error || "Nicht gefunden."}</p>
                </div>
            </div>
        );
    }

    const stages = data.stages || [];
    const activeIdx = stages.findIndex((s) => s.key === data.status);
    const vehicleName = data.vehicle?.title || `${data.vehicle?.brand || ""} ${data.vehicle?.model || ""}`.trim() || "Ihr Fahrzeug";

    return (
        <div className="min-h-screen bg-gray-50" data-testid="order-tracking-public-page">
            {/* Header mit Händler-Branding */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
                    {data.dealer?.logo_url
                        ? <img src={data.dealer.logo_url} alt={data.dealer.company_name} className="h-9 w-auto object-contain" data-testid="order-tracking-dealer-logo" />
                        : <div className="h-9 w-9 rounded-sm bg-[#FFCC00] flex items-center justify-center"><Truck className="w-5 h-5 text-[#111827]" /></div>}
                    <div className="font-bold text-[#111827]">{data.dealer?.company_name}</div>
                </div>
            </div>

            <div className="max-w-xl mx-auto px-4 py-8 space-y-6">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Ihre Fahrzeugbestellung</div>
                    <h1 className="font-display text-2xl font-bold text-[#111827] mt-1">{vehicleName}</h1>
                    {data.vehicle?.order_code && <div className="text-[11px] text-gray-500 mt-0.5">Bestellcode {data.vehicle.order_code}</div>}
                </div>

                {/* ETA */}
                {data.eta && (
                    <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-3 py-2 text-sm font-bold" data-testid="order-tracking-public-eta">
                        <Truck className="w-4 h-4" /> Voraussichtliche Lieferung: {new Date(data.eta).toLocaleDateString("de-DE")}
                    </div>
                )}

                {/* Fortschritt */}
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="order-tracking-public-progress">
                    <ol className="space-y-4">
                        {stages.map((s, i) => {
                            const done = i < activeIdx;
                            const current = i === activeIdx;
                            return (
                                <li key={s.key} className="flex items-start gap-3" data-testid={`order-stage-${s.key}`}>
                                    <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done || current ? "bg-[#111827] text-[#FFCC00]" : "bg-gray-100 text-gray-400"}`}>
                                        {done ? <CheckCircle2 className="w-4 h-4" /> : current ? <Truck className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1 pt-0.5">
                                        <div className={`font-bold text-sm ${current ? "text-[#111827]" : done ? "text-gray-700" : "text-gray-400"}`}>{s.label}</div>
                                        {current && <div className="text-[11px] text-emerald-700 font-bold">Aktueller Status</div>}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>

                {/* Verlauf */}
                {(data.history || []).length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="order-tracking-public-history">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Verlauf</div>
                        <ul className="space-y-2.5">
                            {[...data.history].reverse().map((h, i) => (
                                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                    <span className="text-gray-400 text-[11px] font-mono-num shrink-0 mt-0.5">{h.at ? new Date(h.at).toLocaleDateString("de-DE") : ""}</span>
                                    <span>
                                        <b>{stages.find((s) => s.key === h.status)?.label || h.status}</b>
                                        {h.note ? ` — ${h.note}` : ""}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <p className="text-[11px] text-gray-400 text-center">Fragen zu Ihrer Bestellung? Wenden Sie sich an {data.dealer?.company_name}.</p>
            </div>
        </div>
    );
}
